import { defineReference, group, item, topic } from "../define";

/* Groups and methods of the SubblyCart.js widget reference; see ../define.ts. */

export const cartWidget = defineReference({
  path: "/reference/cart-widget",
  title: "SubblyCart.js widget",
  description:
    "Methods, properties, events, settings, and theming of the SubblyCart.js widget.",
  // Sections of the overview page, in the order they appear there.
  topics: [topic("Globals"), topic("Cookies and storage"), topic("Errors")],
  groups: [
    group(
      "methods",
      "Methods",
      "Open the widget, change the cart, sign a customer in, and set the language and the currency.",
      [
        item(
          "open",
          "Open the widget",
          "subblyCart.open(view?)",
          "Opens the widget on the cart summary, or on the checkout.",
        ),
        item(
          "close",
          "Close the widget",
          "subblyCart.close()",
          "Closes the widget.",
        ),
        item(
          "toggle",
          "Toggle the widget",
          "subblyCart.toggle()",
          "Opens the widget when it is closed, closes it when it is open.",
        ),
        item(
          "load-shop",
          "Load the shop",
          "subblyCart.loadShop()",
          "Re-reads the shop from the API.",
        ),
        item(
          "update-cart",
          "Update the cart",
          "subblyCart.updateCart(payload)",
          "Applies cart-level changes and refreshes the widget, open or closed.",
        ),
        item(
          "configure-item",
          "Configure an item",
          "subblyCart.configureItem(payload)",
          "Adds a product, a bundle, or a survey, and asks the customer for what is missing.",
        ),
        item(
          "add-item",
          "Add an item",
          "subblyCart.addItem(payload)",
          "Adds an item that needs no further input straight to the cart.",
        ),
        item(
          "update-item",
          "Update an item",
          "subblyCart.updateItem(cartItemId, payload)",
          "Changes a line that is already in the cart.",
        ),
        item(
          "remove-item",
          "Remove an item",
          "subblyCart.removeItem(cartItemId)",
          "Removes a line from the cart.",
        ),
        item(
          "apply-coupon",
          "Apply a coupon",
          "subblyCart.applyCoupon(couponCode)",
          "Applies a coupon code to the cart.",
        ),
        item(
          "set-pending-coupon",
          "Queue a coupon",
          "subblyCart.setPendingCoupon(couponCode)",
          "Holds a coupon code and applies it once the cart holds the right products.",
        ),
        item(
          "remove-coupon",
          "Remove the coupon",
          "subblyCart.removeCoupon()",
          "Removes the coupon from the cart.",
        ),
        item(
          "apply-gift-card",
          "Apply a gift card",
          "subblyCart.applyGiftCard(giftCardCode)",
          "Applies a gift card code to the cart.",
        ),
        item(
          "remove-gift-card",
          "Remove the gift card",
          "subblyCart.removeGiftCard()",
          "Removes the gift card from the cart.",
        ),
        item(
          "set-language",
          "Set the language",
          "subblyCart.setLanguage(langCode)",
          "Switches the language of the widget.",
        ),
        item(
          "set-currency",
          "Set the currency",
          "subblyCart.setCurrency(currencyCode)",
          "Switches the currency of the cart and re-prices every line.",
        ),
        item(
          "reset-cart",
          "Reset the cart",
          "subblyCart.resetCart()",
          "Discards the cart and creates an empty one with the same currency.",
        ),
        item(
          "set-settings",
          "Change the settings",
          "subblyCart.setSettings(settings)",
          "Overrides the shop's cart settings at runtime. Keys you omit keep their value.",
        ),
        item(
          "configure-gift",
          "Configure a gift",
          "subblyCart.configureGift(payload, mount)",
          "Mounts the gift view, now or after the current customization.",
        ),
        item(
          "set-customer-data",
          "Prefill customer data",
          "subblyCart.setCustomerData(data)",
          "Prefills the checkout with an email and consent flags for a guest.",
        ),
        item(
          "authenticate",
          "Sign a customer in",
          "subblyCart.authenticate(accessToken?)",
          "Signs a customer in with a Subbly access token.",
        ),
        item(
          "sign-out",
          "Sign the customer out",
          "subblyCart.signOut()",
          "Signs the current customer out of the widget.",
        ),
        item(
          "reload",
          "Reload the cart",
          "subblyCart.reload(cartId?)",
          "Re-reads the cart and the sign-in state from the API.",
        ),
        item(
          "configure",
          "Change the URL config",
          "subblyCart.configure(config)",
          "Merges new URL configuration into the running widget.",
        ),
        item(
          "disable",
          "Disable the widget",
          "subblyCart.disable()",
          "Makes the widget stand down: no panel, no cart button, no checkout.",
        ),
        item(
          "enable",
          "Enable the widget",
          "subblyCart.enable()",
          "Clears the disable override.",
        ),
        item(
          "initialize",
          "Initialize manually",
          "SubblyCart.initialize(config)",
          "Boots the widget by hand. Only needed with `init: false`.",
        ),
      ],
    ),
    group(
      "properties",
      "Properties",
      "Read the cart, the shop, the language and currency in use, the SDK client, and the event emitter.",
      [
        item(
          "cart",
          "Cart",
          "subblyCart.cart",
          "The current cart, as one live object the SDK keeps up to date.",
        ),
        item(
          "shop",
          "Shop",
          "subblyCart.shop",
          "Public information about the shop: branding, currencies, languages, settings, and shipping countries.",
        ),
        item(
          "state",
          "State",
          "subblyCart.state",
          "The currency and the language the widget runs on.",
        ),
        item(
          "sdk",
          "SDK client",
          "subblyCart.sdk",
          "The Subbly.js client the widget uses, for direct Storefront API calls.",
        ),
        item(
          "events",
          "Event emitter",
          "subblyCart.events",
          "The event emitter of the widget, with the event names on `events.type`.",
        ),
      ],
    ),
    group(
      "events",
      "Events",
      "Subscribe to what the widget does: the cart changes, the panel opens, a customer signs in, a purchase completes.",
      [
        item(
          "on",
          "Listen to events",
          "subblyCart.events.on(type, handler)",
          "Subscribes to a widget event.",
        ),
        item(
          "off",
          "Stop listening",
          "subblyCart.events.off(type, handler)",
          "Removes a handler you added.",
        ),
        item(
          "cart-ready",
          "Cart ready",
          "events.type.CART_READY",
          "The widget finished initializing and the cart is loaded.",
        ),
        item(
          "cart-updated",
          "Cart updated",
          "events.type.CART_UPDATED",
          "The cart changed.",
        ),
        item(
          "cart-open",
          "Widget opened",
          "events.type.CART_OPEN",
          "The widget panel opened.",
        ),
        item(
          "cart-close",
          "Widget closed",
          "events.type.CART_CLOSE",
          "The widget panel closed.",
        ),
        item(
          "cart-reset",
          "Cart reset",
          "events.type.CART_RESET",
          "A new cart replaced the old one.",
        ),
        item(
          "email-collected",
          "Email collected",
          "events.type.EMAIL_COLLECTED",
          "The customer's email was captured.",
        ),
        item(
          "purchase-completed",
          "Purchase completed",
          "events.type.PURCHASE_COMPLETED",
          "A purchase succeeded.",
        ),
        item(
          "sign-in",
          "Customer signed in",
          "events.type.SIGN_IN",
          "A customer signed in.",
        ),
        item(
          "sign-up",
          "Customer signed up",
          "events.type.SIGN_UP",
          "A customer account was created at checkout.",
        ),
        item(
          "sign-out",
          "Customer signed out",
          "events.type.SIGN_OUT",
          "The customer signed out.",
        ),
      ],
    ),
    group(
      "settings",
      "Settings",
      "Configure the widget with the config object, wire it to your own page with its settings, and drive it from the page URL.",
      [
        item(
          "config",
          "Configuration keys",
          "window.subblyConfig",
          "Every key of the config object the widget reads at start-up.",
        ),
        item(
          "settings",
          "Settings keys",
          "subblyConfig.settings",
          "Page-integration settings you pass in the config object.",
        ),
        item(
          "query-parameters",
          "URL parameters",
          "?coupon=CODE",
          "Query parameters the widget reads from the page URL.",
        ),
      ],
    ),
    group(
      "theming",
      "Theming",
      "Style the widget with the CSS custom properties it writes on its container.",
      [
        item(
          "css-variables",
          "CSS variables",
          "#subbly-cart-widget { --accent }",
          "The custom properties the widget computes from the cart settings.",
        ),
        item(
          "base-tokens",
          "Base tokens",
          ".SubblyAppContainer { --gray-100 }",
          "The palette the widget's own styles are built on.",
        ),
      ],
    ),
  ],
});
